// acquire — the file types a round may accept for an uploaded document.
//
// A closed catalogue rather than a free-text field, for two reasons. It stops an administrator
// inventing an extension the storage layer will reject at upload time, and it lets the service
// derive `accepted_mime_types` — which the contract requires alongside `accepted_extensions`,
// and which nobody should be asked to type by hand.

/** One selectable format: the extension the API stores, and the MIME types it implies. */
interface FileTypeEntry {
  /** Uppercase, dot-less — the value stored in `accepted_extensions`. */
  extension: string;
  /** Sent as `accepted_mime_types`. More than one where browsers disagree. */
  mimeTypes: readonly string[];
}

/**
 * Ordered by how often an accelerator actually asks for them. Extensions double as their own
 * labels — "PDF" reads the same in French, Arabic and English, so these are data values, not
 * user-facing copy needing translation.
 */
export const FILE_TYPES: readonly FileTypeEntry[] = [
  { extension: "PDF", mimeTypes: ["application/pdf"] },
  {
    extension: "DOCX",
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
  },
  { extension: "DOC", mimeTypes: ["application/msword"] },
  {
    extension: "XLSX",
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
  },
  { extension: "XLS", mimeTypes: ["application/vnd.ms-excel"] },
  {
    extension: "PPTX",
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
  },
  { extension: "PPT", mimeTypes: ["application/vnd.ms-powerpoint"] },
  { extension: "CSV", mimeTypes: ["text/csv"] },
  { extension: "TXT", mimeTypes: ["text/plain"] },
  { extension: "PNG", mimeTypes: ["image/png"] },
  // JPG and JPEG are separate entries even though both are `image/jpeg`. Folding them into one
  // was tidier but wrong: the backend seeds `supporting-documents` with BOTH, so a single entry
  // could not represent a row the server already has, and loading it would fail the enum.
  { extension: "JPG", mimeTypes: ["image/jpeg"] },
  { extension: "JPEG", mimeTypes: ["image/jpeg"] },
  {
    extension: "ZIP",
    mimeTypes: ["application/zip", "application/x-zip-compressed"],
  },
  // Video, for a round that asks for a pitch recording rather than a deck. Worth knowing before
  // enabling it on a round: a phone-shot MP4 runs to tens of megabytes, so the row's « Taille
  // maximale » has to be raised well past the 10 MB default or every upload is refused — and on the
  // 3G budget the applicant surface is built for, that upload is the most expensive thing we ask of
  // a candidat.
  { extension: "MP4", mimeTypes: ["video/mp4"] },
];

/** Every selectable extension, for the format picker's option list. */
export const FILE_TYPE_EXTENSIONS: readonly string[] = FILE_TYPES.map(
  (entry) => entry.extension
);

/**
 * MIME types implied by a set of extensions, de-duplicated. This is what closes the gap the
 * free-text formats field left open: the wire body carries both halves of the contract without
 * an administrator ever seeing a MIME string.
 */
export function mimeTypesFor(extensions: readonly string[]): string[] {
  const selected = new Set(extensions);
  const mimeTypes = FILE_TYPES.filter((entry) =>
    selected.has(entry.extension)
  ).flatMap((entry) => entry.mimeTypes);
  return [...new Set(mimeTypes)];
}

/**
 * Server extensions (lowercase, dot-stripped) into catalogue keys (uppercase).
 *
 * Anything the catalogue does not know is DROPPED rather than carried through: it could not be
 * rendered as an option, and echoing it back on the next PATCH would silently re-assert a value
 * the administrator never saw. Losing it is visible; smuggling it is not.
 */
export function toCatalogueExtensions(
  serverExtensions: readonly string[]
): string[] {
  const known = new Set<string>(FILE_TYPE_EXTENSIONS);
  return serverExtensions
    .map((extension) => extension.replace(/^\./, "").toLocaleUpperCase())
    .filter((extension) => known.has(extension));
}

/** Catalogue keys back to the lowercase form the API stores. */
export function toServerExtensions(extensions: readonly string[]): string[] {
  return extensions.map((extension) => extension.toLocaleLowerCase());
}
